/* eslint-disable react-hooks/exhaustive-deps */
import classNames from 'classnames'
import dayjs from 'dayjs'
import {t} from 'i18next'

import {Button} from 'primereact/button'
import {Calendar} from 'primereact/calendar'
import {Dropdown, DropdownChangeParams} from 'primereact/dropdown'
import {Toast} from 'primereact/toast'
import {Tooltip} from 'primereact/tooltip'
import React, {useEffect, useRef, useState} from 'react'
import {Controller, FormProvider, useForm} from 'react-hook-form'
import {JournalRythmCt} from '../../../../model/enums/enums'
import {ApprenticeBody, Person} from '../../../../model/person.model'
import {apprenticeUpdated} from '../../../../redux/basic/apprentice/apprenticeReducer'
import {useAppDispatch, useAppSelector} from '../../../../redux/hooks'

import {addApprenticeToDb, updateApprenticeInDb} from '../../../../services/api/mokaiApiService'
import {findApprenticeById} from '../../../../util/arraySearch'
import {getFormattedDateForMySQL} from '../../../../util/formatting'
import {getFormErrorMessage, getRequiredFormMessage} from '../../../util/ErrorText'
import {getMonthNavigatorTemplate, getYearNavigatorTemplate} from '../../../util/form/calendar/calendar-elements'
import {ProfessionDropdown} from '../../../util/form/dropdown/profession-dropdown/ProfessionDropdown'

const setDefaultStartDate = (): Date => {
	return dayjs(`8.1.${new Date().getFullYear()}`).toDate()
}

const setDefaultEndDate = (): Date => {
	return dayjs(`7.31.${new Date().getFullYear() + 3}`).toDate()
}

const threeYearsLater = (): string => {
	let date = new Date()
	date.setFullYear(date.getFullYear() + 3)
	return dayjs(date).format(t<string>('locales.dateFormat'))
}

const ryhtmOptions = [
	{
		label: t<string>('apprentice.journal_rythm.enum.0'),
		value: JournalRythmCt.Weekly
	},
	{
		label: t<string>('apprentice.journal_rythm.enum.1'),
		value: JournalRythmCt.Monthly
	}
]

// Form that displays editably the data of an apprentice
// to be used as top level form
export const ApprenticeData = (props: { person: Person }): JSX.Element => {
	const dispatcher = useAppDispatch()
	const toast = useRef<Toast>(null)

	const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false)

	const defaultApprentice: ApprenticeBody = {
		apprentice_id: undefined,
		id_profession: undefined,
		id_speciality: undefined,
		contract_start: setDefaultStartDate(),
		contract_end: setDefaultEndDate(),
		id_person: props.person.person_id,
		journal_rythm: JournalRythmCt.Weekly,
		journal_start: setDefaultStartDate()
	}

	const methods = useForm({
		reValidateMode: 'onChange',
		mode: 'onSubmit',
		shouldFocusError: false,
		defaultValues: defaultApprentice
	})

	const apprentices = useAppSelector((state) => state.apprentices)

	// loads the current apprentice into the form
	const findApprentice = (person_id: number): void => {
		const searchedApprentice = apprentices.find(
			(a) => a.apprentice?.id_person === person_id
		)
		if (searchedApprentice) {
			methods.reset(searchedApprentice.apprentice)
			if (!searchedApprentice.apprentice?.journal_start) {
				methods.setValue(
					'journal_start',
					new Date(searchedApprentice.apprentice?.contract_start)
				)
			}
		}
	}

	useEffect(() => {
		if (!props.person.person_id) {
			console.warn(
				'[Apprentice Data] person id not defined, something went wrong'
			)
			return
		}
		findApprentice(props.person.person_id)
	}, [])

	useEffect(() => {
		const subscription = methods.watch(async (value, {name, type}) => {
			// undefined is needed for phone adds
			if (type === 'change' || type === undefined) {
				setHasUnsavedChanges(true)
			}
		})
		return () => subscription.unsubscribe()
	}, [])

	const onSubmit = async (formData: ApprenticeBody) => {
		// condition has to be true here
		// necessary for db formatting
		if (formData.contract_start) {
			formData.contract_start = getFormattedDateForMySQL(
				formData.contract_start
			)
		}

		if (formData.contract_end) {
			formData.contract_end = getFormattedDateForMySQL(formData.contract_end)
		}

		if (formData.journal_start) {
			formData.journal_start = getFormattedDateForMySQL(formData.journal_start)
		}

		// we need to create a new apprentice
		if (!formData.apprentice_id) {
			const res = await addApprenticeToDb(formData)
			if (!res.data) {
				console.warn('[ApprenticeData] Creating an Entry was not successful')
				return
			}

			const apprenticePerson = findApprenticeById(
				res.data.apprentice_id,
				apprentices
			)
			if (apprenticePerson) {
				const apprenticePersonCopy = {
					...apprenticePerson,
					apprentice: {...res.data}
				}
				dispatcher(apprenticeUpdated(apprenticePersonCopy))
			}

			methods.reset(res.data)
		} else {
			// we already have an apprentice id
			const res = await updateApprenticeInDb(formData.apprentice_id, formData)

			if (!res.data) {
				console.warn('[ApprenticeDatas] Updating an Entry was not successful')
				return
			}

			let apprenticePerson = findApprenticeById(
				res.data.apprentice_id,
				apprentices
			)
			if (apprenticePerson) {
				const apprenticePersonCopy = {
					...apprenticePerson,
					apprentice: {...res.data}
				}
				dispatcher(apprenticeUpdated(apprenticePersonCopy))
			}

			methods.reset(res.data)
		}
		setHasUnsavedChanges(false)
		toast.current?.show({
			closable: true,
			severity: 'success',
			summary: t<string>('core.action.save.success.title'),
			detail: t<string>('hrm.save_education_data'),
			life: 4000
		})
	}

	return (
		<>
			<div className='mt-2'>
				<FormProvider {...methods}>
					<form onSubmit={methods.handleSubmit(onSubmit)}>
						<div>
							<div className='formgrid grid p-fluid '>
								<ProfessionDropdown/>
								<div className='col-12 md:col-3 field'>
									<label
										className={classNames({
											'p-error': methods.formState.errors.contract_start
										})}
										htmlFor='contract_start'
									>
										{t<string>('apprentice.contract_start')}
									</label>
									<Controller
										name='contract_start'
										control={methods.control}
										rules={{
											required: getRequiredFormMessage(
												t<string>('apprentice.contract_start')
											)
										}}
										render={({field}) => (
											<Calendar
												appendTo={document.body}
												id={field.name}
												dateFormat={'dd.mm.yy'}
												mask='99.99.9999'
												monthNavigator
												monthNavigatorTemplate={getMonthNavigatorTemplate}
												yearNavigator
												showWeek={true}
												yearRange='2018:2050'
												yearNavigatorTemplate={getYearNavigatorTemplate}
												showIcon
												value={field.value ? new Date(field.value) : undefined}
												onChange={(e) => {
													field.onChange(e.value)
												}}
												showButtonBar={true}
												placeholder={dayjs(new Date()).format(
													t<string>('locales.dateFormat')
												)}
											/>
										)}
									/>
									{getFormErrorMessage(
										methods.formState.errors,
										'contract_start'
									)}
								</div>
								<div className='col-12 md:col-3 field'>
									<label htmlFor='contract_end'>
										{t<string>('apprentice.contract_end')}
									</label>
									<Controller
										name='contract_end'
										control={methods.control}
										render={({field}) => (
											<Calendar
												appendTo={document.body}
												id={field.name}
												dateFormat={'dd.mm.yy'}
												value={field.value ? new Date(field.value) : undefined}
												showWeek={true}
												onChange={(e) => field.onChange(e.value)}
												monthNavigator
												monthNavigatorTemplate={getMonthNavigatorTemplate}
												yearNavigator
												yearRange='2020:2054'
												yearNavigatorTemplate={getYearNavigatorTemplate}
												showButtonBar
												showIcon
												placeholder={threeYearsLater()}
											/>
										)}
									/>
									{getFormErrorMessage(
										methods.formState.errors,
										'contract_end'
									)}
								</div>
								<div className='col-12 md:col-3 field'>
									<label
										className={classNames({
											'p-error': methods.formState.errors.contract_start
										})}
										htmlFor='contract_start'
									>
										{t<string>('apprentice.journal_rythm.title')}
									</label>
									<Controller
										name='journal_rythm'
										control={methods.control}
										render={({field}) => (
											<Dropdown
												appendTo={document.body}
												id={'SizeDropdown'}
												value={field.value || ''}
												options={ryhtmOptions}
												defaultValue={JournalRythmCt.Weekly}
												onChange={(changeParams: DropdownChangeParams) => {
													field.onChange(changeParams.value)
												}}
												placeholder={t<string>(
													'apprentice.journal_rythm.enum.0'
												)}
											/>
										)}
									/>
								</div>
								<div className='col-12 md:col-3 field'>
									<label htmlFor='contract_end'>
										{t<string>('apprentice.journal_from')}
										<span
											className={
												'fa-solid fa-question-circle ml-2 icon-gray journal-tooltip'
											}
										/>
									</label>
									<Tooltip
										target={'.journal-tooltip'}
										content={t<string>('apprentice.journal_from_tooltip')}
										showDelay={500}
									/>
									<Controller
										name='journal_start'
										control={methods.control}
										render={({field}) => (
											<Calendar
												appendTo={document.body}
												id={field.name}
												dateFormat={'dd.mm.yy'}
												value={field.value ? new Date(field.value) : undefined}
												onChange={(e) => field.onChange(e.value)}
												showWeek={true}
												monthNavigator
												monthNavigatorTemplate={getMonthNavigatorTemplate}
												yearNavigator
												yearRange='2020:2054'
												yearNavigatorTemplate={getYearNavigatorTemplate}
												showButtonBar
												showIcon
											/>
										)}
									/>
									{getFormErrorMessage(
										methods.formState.errors,
										'contract_end'
									)}
								</div>
							</div>
							<div className={'mt-2 mb-2 flex gap-2 justify-content-end'}>
								<Button
									type={'submit'}
									disabled={!hasUnsavedChanges}
									label={t<string>('core.save')}
								/>
							</div>
						</div>
					</form>
				</FormProvider>
			</div>
			<Toast ref={toast} position={'bottom-right'}/>
		</>
	)
}